import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import Iconify from 'src/components/iconify';
import api from 'src/services/BaseService';
import Services from 'src/services/Services';
import { tokenService } from 'src/services';
import {
  ACTION_TYPES,
  formatDateTime,
  getActionMeta,
  normalizeMallAlertBlockLog,
} from './mallAlertBlockData';

const actionOptions = [
  { value: 'all', label: 'Tüm İşlemler' },
  { value: ACTION_TYPES.WARNING_ADDED, label: 'Uyarı Eklendi' },
  { value: ACTION_TYPES.WARNING_REMOVED, label: 'Uyarı Kaldırıldı' },
  { value: ACTION_TYPES.BLOCKED, label: 'Blokaja Alındı' },
  { value: ACTION_TYPES.UNBLOCKED, label: 'Blokaj Kaldırıldı' },
];

export default function MallAlertBlockRecords() {
  const mallID = localStorage.getItem('mallID') || '';
  const [selectedAction, setSelectedAction] = useState('all');
  const [userQuery, setUserQuery] = useState('');
  const [storeQuery, setStoreQuery] = useState('');
  const [corporateCodeQuery, setCorporateCodeQuery] = useState('');
  const [accountCodeQuery, setAccountCodeQuery] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!mallID) {
      setRecords([]);
      return;
    }

    setLoading(true);
    const token = tokenService.getUser()?.token;

    Services.ListBranchWarningsBlockLogs(token)
      .then((response) => {
        if (response.data.status) {
          const result = Array.isArray(response.data.result)
            ? response.data.result
            : [];
          setRecords(result.map(normalizeMallAlertBlockLog));
        } else {
          setRecords([]);
        }
      })
      .catch(() => {
        setRecords([]);
        toast.error('Uyarı ve blokaj kayıtları alınırken bir hata oluştu.');
      })
      .finally(() => setLoading(false));
  }, [mallID]);

  const filteredRecords = useMemo(() => {
    const normalizedUser = userQuery.trim().toLocaleLowerCase('tr-TR');
    const normalizedStore = storeQuery.trim().toLocaleLowerCase('tr-TR');
    const normalizedCorporateCode = corporateCodeQuery
      .trim()
      .toLocaleLowerCase('tr-TR');
    const normalizedAccountCode = accountCodeQuery
      .trim()
      .toLocaleLowerCase('tr-TR');

    return records.filter((record) => {
      const actionMatch =
        selectedAction === 'all' ? true : record.type === selectedAction;
      const userMatch = normalizedUser
        ? String(record.userName)
            .toLocaleLowerCase('tr-TR')
            .includes(normalizedUser)
        : true;
      const storeMatch = normalizedStore
        ? `${record.storeTitle} ${record.mallTitle}`
            .toLocaleLowerCase('tr-TR')
            .includes(normalizedStore)
        : true;

      const corporateCodeMatch = normalizedCorporateCode
        ? String(record.companyCorporationalCode)
            .toLocaleLowerCase('tr-TR')
            .includes(normalizedCorporateCode)
        : true;
      const accountCodeMatch = normalizedAccountCode
        ? String(record.accountCode)
            .toLocaleLowerCase('tr-TR')
            .includes(normalizedAccountCode)
        : true;

      return (
        actionMatch &&
        userMatch &&
        storeMatch &&
        corporateCodeMatch &&
        accountCodeMatch
      );
    });
  }, [
    records,
    selectedAction,
    storeQuery,
    userQuery,
    corporateCodeQuery,
    accountCodeQuery,
  ]);

  const handleExport = () => {
    if (filteredRecords.length === 0) return;

    const rows = filteredRecords.map((record) => ({
      createdAt: formatDateTime(record.createdAt),
      action: getActionMeta(record.type).label,
      userName: record.userName,
      mallTitle: record.mallTitle || '-',
      storeTitle: record.storeTitle,
      companyCorporationalCode: record.companyCorporationalCode || '-',
      accountCode: record.accountCode || '-',
      message: record.message || '-',
    }));

    api
      .post('excel/export', {
        type: 'excel',
        content: rows,
        header: {
          'Tarih / Saat': 'string',
          İşlem: 'string',
          Kullanıcı: 'string',
          AVM: 'string',
          Mağaza: 'string',
          'Cari Kod': 'string',
          'Muhasebe Kodu': 'string',
          'Mesaj Detayı': 'string',
        },
        sheetTitle: 'Uyarı ve Blokaj Kayıtları',
      })
      .then((response) => {
        if (response.data.status) {
          const newWindow = window.open(response.data.result, '_blank');
          if (
            !newWindow ||
            newWindow.closed ||
            typeof newWindow.closed === 'undefined'
          ) {
            toast.error(
              'Pop-up engellendi. Lütfen pop-up engelleyicisini devre dışı bırakın.'
            );
          }
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  return (
    <>
      <Helmet>
        <title>Uyarı ve Blokaj Kayıtları</title>
      </Helmet>

      <Stack spacing={3} sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Uyarı ve Blokaj Kayıtları
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Seçili AVM için oluşturulan uyarı, mesaj ve blokaj geçmişini
            görüntüleyebilirsiniz.
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 3 }}>
          <Stack spacing={2} sx={{ p: 2.25 }}>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1}
              alignItems={{ xs: 'stretch', lg: 'center' }}
              sx={{
                p: 1.5,
                border: '1px solid #E2E8F0',
                borderRadius: '14px',
                backgroundColor: '#FFFFFF',
              }}
            >
              <TextField
                select
                placeholder="Tüm İşlemler"
                size="small"
                value={selectedAction}
                onChange={(event) => setSelectedAction(event.target.value)}
                sx={{
                  minWidth: { lg: 220 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
                SelectProps={{ native: true }}
              >
                {actionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </TextField>
              <TextField
                placeholder="Kullanıcı ara..."
                size="small"
                fullWidth
                value={userQuery}
                onChange={(event) => setUserQuery(event.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
              />
              <TextField
                placeholder="Mağaza veya AVM ara..."
                size="small"
                fullWidth
                value={storeQuery}
                onChange={(event) => setStoreQuery(event.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
              />
              <TextField
                placeholder="Cari kod ara..."
                size="small"
                fullWidth
                value={corporateCodeQuery}
                onChange={(event) => setCorporateCodeQuery(event.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
              />
              <TextField
                placeholder="Muhasebe kodu ara..."
                size="small"
                fullWidth
                value={accountCodeQuery}
                onChange={(event) => setAccountCodeQuery(event.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleExport}
                disabled={filteredRecords.length === 0}
                startIcon={<Iconify icon="solar:download-outline" />}
                sx={{
                  minWidth: { xs: '100%', lg: 140 },
                  width: { xs: '100%', lg: 'auto' },
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  height: 32,
                  px: 2.25,
                  borderRadius: '9px',
                  boxShadow: 'none',
                  textTransform: 'none',
                  fontWeight: 600,
                  '& .MuiButton-startIcon': {
                    mr: 0.75,
                  },
                }}
              >
                Excel&apos;e Aktar
              </Button>
            </Stack>

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {filteredRecords.length} kayıt listeleniyor
            </Typography>

            <TableContainer
              sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}
            >
              <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 1100 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableCell sx={{ py: 1.25, width: '11%' }}>
                      Tarih / Saat
                    </TableCell>
                    <TableCell sx={{ py: 1.25, width: '14%' }}>İşlem</TableCell>
                    <TableCell sx={{ py: 1.25, width: '11%' }}>
                      Kullanıcı
                    </TableCell>
                    <TableCell sx={{ py: 1.25, width: '13%' }}>AVM</TableCell>
                    <TableCell sx={{ py: 1.25, width: '14%' }}>Mağaza</TableCell>
                    <TableCell sx={{ py: 1.25, width: '10%' }}>
                      Cari Kod
                    </TableCell>
                    <TableCell sx={{ py: 1.25, width: '11%' }}>
                      Muhasebe Kodu
                    </TableCell>
                    <TableCell sx={{ py: 1.25, width: '16%' }}>
                      Mesaj Detayı
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <CircularProgress size={18} />
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary' }}
                          >
                            Kayıtlar yükleniyor...
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}

                  {!loading &&
                    filteredRecords.map((record) => {
                      const actionMeta = getActionMeta(record.type);

                      return (
                        <TableRow
                          key={record.id}
                          hover
                          sx={{
                            '& td': {
                              py: 1.8,
                              borderColor: '#F3F4F6',
                            },
                          }}
                        >
                          <TableCell>
                            {formatDateTime(record.createdAt)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={actionMeta.label}
                              icon={
                                <Iconify icon={actionMeta.icon} width={16} />
                              }
                              sx={{
                                borderRadius: '999px',
                                backgroundColor: actionMeta.color,
                                color: actionMeta.textColor,
                                '& .MuiChip-icon': {
                                  color: actionMeta.textColor,
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell>{record.userName}</TableCell>
                          <TableCell>{record.mallTitle || '-'}</TableCell>
                          <TableCell>{record.storeTitle}</TableCell>
                          <TableCell>
                            {record.companyCorporationalCode || '-'}
                          </TableCell>
                          <TableCell>{record.accountCode || '-'}</TableCell>
                          <TableCell>{record.message || '-'}</TableCell>
                        </TableRow>
                      );
                    })}

                  {!loading && filteredRecords.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        Kayıt bulunamadı.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Card>
      </Stack>
    </>
  );
}
